<div class="text-center mb-3">
	<h1 class="mb-0">EOM Processing</h1>
</div>

<!-- 1. EOM charge file upload -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<div>
			<h4 class="mb-1">1. Upload the EOM Charge File</h4>
			<small class="text-muted">
				Upload the combined EOM charge file (.csv with CompanyID, UnitID, ChargeType, Category, SubCatagory, Note, ChargeAmount).
				Everything in transport.EOMChargeProcess is deleted first, then the file's rows are saved.
				If any row can't be read, the whole file is rejected and the table is left as it was.
			</small>
		</div>

		<div class="alert alert-warning mt-3 mb-0" *ngIf="isRunning">
			A process is running; upload the next file after it finishes.
		</div>

		<div class="row mt-3">
			<div class="col-md-12" *ngIf="!file">
				<div
					ng2FileDrop
					[ngClass]="fileDragged ? 'nv-file-over' : ''"
					[uploader]="uploader"
					class="eom-drop-zone"
					(dragover)="onDragover()"
					(dragleave)="onDragout()"
					(mouseleave)="onDragout()"
					data-testid="eom-processing-drop">
					<label
						for="eom-processing-file"
						role="button"
						aria-label="Choose the EOM charge file"
						class="eom-file-label m-0 w-100 h-100 d-flex align-items-center justify-content-center">
						<i class="fa fa-cloud-upload mr-2" aria-hidden="true"></i> Drop the EOM charge file here, or click to choose it
					</label>
					<input
						id="eom-processing-file"
						type="file"
						accept=".csv"
						ng2FileSelect
						[uploader]="uploader"
						data-testid="eom-processing-file" />
				</div>
			</div>

			<div class="col-md-3" *ngIf="file">
				<div class="text-muted">File</div>
				<div class="font-weight-bold eom-ellipsis" [title]="file.name">{{ file.name }}</div>
				<div class="text-muted mt-2">Size</div>
				<div class="font-weight-bold">{{ file.size / 1024 | number : '1.0-1' }} KB</div>
				<div class="text-muted mt-2">Rows</div>
				<div class="font-weight-bold">{{ previewTotalRows | number }}</div>
				<button
					type="button"
					class="btn btn-primary mt-3 mr-2"
					[disabled]="uploading || isRunning"
					(click)="upload()"
					data-testid="eom-processing-upload">
					<i class="fa" [ngClass]="uploading ? 'fa-spinner fa-spin' : 'fa-upload'" aria-hidden="true"></i>
					Upload
				</button>
				<button
					type="button"
					class="btn btn-secondary mt-3"
					[disabled]="uploading"
					(click)="resetFile()"
					data-testid="eom-processing-cancel">
					Cancel
				</button>
			</div>

			<div class="col-md-9" *ngIf="file && previewColumns.length > 0">
				<div class="font-weight-bold">
					Preview
					<small class="text-muted" *ngIf="previewTotalRows > previewRows.length">(first {{ previewRows.length }} of {{ previewTotalRows | number }} rows)</small>
				</div>
				<div class="eom-preview">
					<table class="table table-sm table-striped mb-0">
						<thead>
							<tr>
								<th *ngFor="let column of previewColumns" class="text-nowrap">{{ column }}</th>
							</tr>
						</thead>
						<tbody>
							<tr *ngFor="let row of previewRows">
								<td *ngFor="let column of previewColumns; let i = index" class="text-nowrap">{{ row[i] }}</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<div class="alert alert-success mt-3 mb-0" *ngIf="uploadResult" data-testid="eom-processing-result">
			{{ uploadResult.summary }}
		</div>
		<div class="alert alert-danger mt-3 mb-0 eom-error" *ngIf="uploadError" data-testid="eom-processing-error">
			{{ uploadError }}
		</div>
	</div>
</div>

<!-- 2. Permit / non-permit processing -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<div>
			<h4 class="mb-1">2. Process the Charges</h4>
			<small class="text-muted">
				Each process gives every company one invoice (status 6; merged into an existing status 6 invoice with the same number),
				one invoice line per charge, and the charges already attached to their lines. Each company runs in its own transaction;
				if one fails it is rolled back and the run stops there. Companies not in transport.Company are skipped. Rows are marked
				processed as each company completes, so running a process again only picks up what is left.
			</small>
			<ul class="small text-muted mt-2 mb-0">
				<li><b>Permits</b> (Category contains "Permit"): PER-yyMMdd-C&lt;CompanyID&gt; invoices, no journal entries, then queued for delivery (DownloadLogs and email).</li>
				<li><b>Non-Permits</b> (Samsara, Pedigree, Safety / Compliance): EOM-yyMMdd-C&lt;CompanyID&gt; invoices, with journal entries for every charge.</li>
			</ul>
		</div>

		<div class="form-inline mt-3">
			<label for="eom-charge-date" class="mr-2">Charge date</label>
			<input
				id="eom-charge-date"
				type="date"
				class="form-control mr-3 mb-2"
				[(ngModel)]="chargeDate"
				[disabled]="isRunning"
				data-testid="eom-processing-charge-date" />
			<button
				type="button"
				class="btn btn-primary mr-2 mb-2"
				[disabled]="isRunning || startingProcess || permitRowsToProcess === 0"
				(click)="process('permits')"
				data-testid="eom-processing-permits">
				<i class="fa fa-cogs" aria-hidden="true"></i>
				Process Permits ({{ permitRowsToProcess | number }})
			</button>
			<button
				type="button"
				class="btn btn-primary mb-2"
				[disabled]="isRunning || startingProcess || nonPermitRowsToProcess === 0"
				(click)="process('nonpermits')"
				data-testid="eom-processing-nonpermits">
				<i class="fa fa-cogs" aria-hidden="true"></i>
				Process Non-Permits ({{ nonPermitRowsToProcess | number }})
			</button>
		</div>
		<small class="text-muted d-block" *ngIf="otherRowsUnprocessed > 0">
			{{ otherRowsUnprocessed | number }} unprocessed row(s) have a category neither process handles.
		</small>

		<div class="eom-running mt-3 mb-0" *ngIf="isRunning" data-testid="eom-processing-running">
			<i class="fa fa-spinner fa-spin mr-2" aria-hidden="true"></i>
			{{ kindName(run.kind) }} process running for charge date {{ run.chargeDate | date : 'MM/dd/yyyy' }}
			(started {{ run.startedOn | date : 'short' }}). You can leave this page; it keeps running on the server.
		</div>

		<ng-container *ngIf="run && run.isFinished">
			<hr />
			<h5 class="mb-2">
				Last run: {{ kindName(run.kind) }}, charge date {{ run.chargeDate | date : 'MM/dd/yyyy' }}
				<small class="text-muted">finished {{ run.finishedOn | date : 'short' }}</small>
			</h5>

			<div class="alert alert-danger eom-error" *ngIf="run.error" data-testid="eom-processing-run-error">{{ run.error }}</div>
			<div class="alert alert-success" *ngIf="run.successful">Finished without errors.</div>

			<div class="row" *ngIf="run.result && run.result.summary">
				<div class="col-md-3 col-6 mb-2">
					<div class="text-muted">Companies processed</div>
					<div class="font-weight-bold">{{ run.result.summary.companiesProcessed | number }} of {{ run.result.summary.companiesToProcess | number }}</div>
				</div>
				<div class="col-md-3 col-6 mb-2">
					<div class="text-muted">Charges / lines created</div>
					<div class="font-weight-bold" [ngClass]="run.result.summary.chargesCreated !== run.result.summary.linesCreated ? 'text-danger' : ''">
						{{ run.result.summary.chargesCreated | number }} / {{ run.result.summary.linesCreated | number }}
					</div>
				</div>
				<div class="col-md-3 col-6 mb-2">
					<div class="text-muted">Charge amount / tax</div>
					<div class="font-weight-bold">
						{{ run.result.summary.chargeAmountTotal | currency : 'USD' : 'symbol' : '1.2-2' }} /
						{{ run.result.summary.taxAmountTotal | currency : 'USD' : 'symbol' : '1.2-2' }}
					</div>
				</div>
				<div class="col-md-3 col-6 mb-2">
					<div class="text-muted">Invoices</div>
					<div class="font-weight-bold">{{ run.result.summary.invoiceCount | number }}</div>
				</div>
				<div class="col-md-3 col-6 mb-2">
					<div class="text-muted">Orphan charges</div>
					<div class="font-weight-bold" [ngClass]="run.result.summary.orphanCharges > 0 ? 'text-danger' : ''">{{ (run.result.summary.orphanCharges || 0) | number }}</div>
				</div>
				<div class="col-md-3 col-6 mb-2">
					<div class="text-muted">Companies skipped</div>
					<div class="font-weight-bold" [ngClass]="run.result.summary.companiesSkipped > 0 ? 'text-warning' : ''">{{ run.result.summary.companiesSkipped | number }}</div>
				</div>
				<div class="col-md-3 col-6 mb-2">
					<div class="text-muted">Rows left unprocessed</div>
					<div class="font-weight-bold">{{ run.result.summary.rowsLeftUnprocessed | number }}</div>
				</div>
				<div class="col-md-3 col-6 mb-2" *ngIf="run.kind === 'permits'">
					<div class="text-muted">DownloadLogs added / emails queued</div>
					<div class="font-weight-bold">{{ (run.result.summary.invoicesAddedToDownloadLogs || 0) | number }} / {{ (run.result.summary.emailsQueued || 0) | number }}</div>
				</div>
			</div>

			<ng-container *ngIf="run.result && run.result.skippedCompanies.length > 0">
				<div class="font-weight-bold mt-3">Skipped companies (not in transport.Company)</div>
				<div class="eom-result-table">
					<table class="table table-sm table-striped mb-0">
						<thead>
							<tr><th>Company ID</th><th>Rows</th><th>Amount</th><th>Reason</th></tr>
						</thead>
						<tbody>
							<tr *ngFor="let s of run.result.skippedCompanies">
								<td>{{ s.companyId }}</td>
								<td>{{ s.sourceRows | number }}</td>
								<td>{{ s.totalChargeAmount | currency : 'USD' : 'symbol' : '1.2-2' }}</td>
								<td>{{ s.reason }}</td>
							</tr>
						</tbody>
					</table>
				</div>
			</ng-container>

			<ng-container *ngIf="run.result && run.result.journalEntryProblems.length > 0">
				<div class="font-weight-bold mt-3 text-danger">Journal entry problems</div>
				<div class="eom-result-table">
					<table class="table table-sm table-striped mb-0">
						<thead>
							<tr><th>Charge ID</th><th>Company ID</th><th>Amount</th><th>Tax</th><th>Entries</th><th>Credits</th><th>Debits</th><th>Missing COA</th><th>Issue</th></tr>
						</thead>
						<tbody>
							<tr *ngFor="let p of run.result.journalEntryProblems">
								<td>{{ p.chargeId }}</td>
								<td>{{ p.companyId }}</td>
								<td>{{ p.amount | number : '1.2-4' }}</td>
								<td>{{ p.tax | number : '1.2-4' }}</td>
								<td>{{ p.entryCount }}</td>
								<td>{{ p.totalCredits | number : '1.2-4' }}</td>
								<td>{{ p.totalDebits | number : '1.2-4' }}</td>
								<td>{{ p.entriesMissingCOA }}</td>
								<td>{{ p.issue }}</td>
							</tr>
						</tbody>
					</table>
				</div>
			</ng-container>

			<ng-container *ngIf="run.result && run.result.invoices.length > 0">
				<div class="font-weight-bold mt-3">
					Invoices <small class="text-muted" *ngIf="run.kind === 'permits'">(every PER invoice for this charge date)</small>
				</div>
				<div class="eom-result-table">
					<table class="table table-sm table-striped mb-0">
						<thead>
							<tr>
								<th>Invoice ID</th><th>Invoice Number</th><th>Company ID</th><th>Status</th><th>Amount Due</th><th>Tax Due</th><th>Lines</th>
								<th *ngIf="run.kind === 'permits'">Download Log</th>
								<th *ngIf="run.kind === 'permits'">Email</th>
							</tr>
						</thead>
						<tbody>
							<tr *ngFor="let i of run.result.invoices">
								<td>{{ i.invoiceId }}</td>
								<td class="text-nowrap">{{ i.invoiceNumber }}</td>
								<td>{{ i.companyId }}</td>
								<td>{{ i.invoiceStatusTypeId }}</td>
								<td>{{ i.totalAmountDue | currency : 'USD' : 'symbol' : '1.2-2' }}</td>
								<td>{{ i.totalTaxAmountDue | currency : 'USD' : 'symbol' : '1.2-2' }}</td>
								<td>{{ i.lineCount | number }}</td>
								<td *ngIf="run.kind === 'permits'" [ngClass]="i.downloadLogStatus === 'Queued in DownloadLogs' ? '' : 'text-muted'">{{ i.downloadLogStatus }}</td>
								<td *ngIf="run.kind === 'permits'" [ngClass]="i.emailStatus === 'Email queued' ? '' : 'text-muted'">{{ i.emailStatus }}</td>
							</tr>
						</tbody>
					</table>
				</div>
			</ng-container>
		</ng-container>
	</div>
</div>

<!-- Saved rows -->
<div class="col-lg-12 mb-4">
	<div class="shadow p-3">
		<div class="d-flex justify-content-between align-items-start">
			<div>
				<h4 class="mb-1">Uploaded Charges</h4>
				<small class="text-muted">Rows in transport.EOMChargeProcess<span *ngIf="charges.length > 0"> from {{ charges[0].sourceFileName }}, uploaded {{ charges[0].importedDate | date : 'short' }}</span>.</small>
			</div>
			<button
				type="button"
				class="btn btn-outline-secondary btn-sm"
				[disabled]="loadingCharges"
				(click)="loadCharges()"
				data-testid="eom-processing-refresh">
				<i class="fa fa-refresh" [ngClass]="loadingCharges ? 'fa-spin' : ''" aria-hidden="true"></i>
				Refresh
			</button>
		</div>

		<div class="form-inline mt-3">
			<select
				class="form-control mr-2 mb-2"
				[(ngModel)]="statusFilter"
				(ngModelChange)="applyFilter()"
				aria-label="Processed status"
				data-testid="eom-processing-status-filter">
				<option value="">All rows</option>
				<option value="unprocessed">Not processed</option>
				<option value="processed">Processed</option>
				<option value="notdelivered">Permits not in Download Logs and no email queued</option>
			</select>
			<input
				type="text"
				class="form-control mb-2 eom-filter"
				placeholder="Filter"
				aria-label="Filter charges"
				[(ngModel)]="chargeFilter"
				(ngModelChange)="applyFilter()"
				data-testid="eom-processing-filter" />
		</div>

		<div class="mb-2">
			<b>{{ filteredCharges.length | number }}</b> row(s), total <b>{{ filteredTotal | currency : 'USD' : 'symbol' : '1.2-2' }}</b>
			<span class="ml-3 eom-not-delivered-legend" *ngIf="notDeliveredCount > 0">
				<b>{{ notDeliveredCount | number }}</b> processed permit row(s) highlighted: invoice not in Download Logs and no email queued
				<button
					type="button"
					class="btn btn-link btn-sm p-0 ml-2 align-baseline"
					(click)="toggleNotDeliveredFilter()"
					data-testid="eom-processing-filter-highlighted">
					{{ statusFilter === 'notdelivered' ? 'Show all rows' : 'Show only these' }}
				</button>
			</span>
		</div>

		<ngx-datatable
			class="material striped eom-charges"
			[rows]="filteredCharges"
			[columnMode]="'force'"
			[headerHeight]="40"
			[footerHeight]="0"
			[rowHeight]="32"
			[scrollbarV]="true"
			[scrollbarH]="true"
			[loadingIndicator]="loadingCharges"
			[rowClass]="getChargeRowClass"
			data-testid="eom-processing-charges">
			<ngx-datatable-column name="Company ID" prop="companyId" [width]="100"></ngx-datatable-column>
			<ngx-datatable-column name="Unit ID" prop="unitId" [width]="80"></ngx-datatable-column>
			<ngx-datatable-column name="Charge Type" prop="chargeType" [width]="120"></ngx-datatable-column>
			<ngx-datatable-column name="Category" prop="category" [width]="150"></ngx-datatable-column>
			<ngx-datatable-column name="Sub Category" prop="subCategory" [width]="150"></ngx-datatable-column>
			<ngx-datatable-column name="Note" prop="note" [width]="380">
				<ng-template let-value="value" ngx-datatable-cell-template>
					<span [title]="value">{{ value }}</span>
				</ng-template>
			</ngx-datatable-column>
			<ngx-datatable-column name="Charge Amount" prop="chargeAmount" [width]="120">
				<ng-template let-value="value" ngx-datatable-cell-template>
					{{ value | currency : 'USD' : 'symbol' : '1.2-2' }}
				</ng-template>
			</ngx-datatable-column>
			<ngx-datatable-column name="Processed" prop="isProcessed" [width]="100">
				<ng-template let-value="value" ngx-datatable-cell-template>
					<span [ngClass]="value ? 'text-success' : 'text-muted'">{{ value ? 'Yes' : 'No' }}</span>
				</ng-template>
			</ngx-datatable-column>
			<ngx-datatable-column name="Invoice ID" prop="invoiceId" [width]="100"></ngx-datatable-column>
			<ngx-datatable-column name="Invoice Number" prop="invoiceNumber" [width]="190"></ngx-datatable-column>
			<ngx-datatable-column name="Download Log" prop="inDownloadLogs" [width]="110">
				<ng-template let-row="row" ngx-datatable-cell-template>
					<span *ngIf="row.isProcessed" [ngClass]="row.inDownloadLogs ? 'text-success' : 'text-muted'">{{ row.inDownloadLogs ? 'Yes' : 'No' }}</span>
				</ng-template>
			</ngx-datatable-column>
			<ngx-datatable-column name="Email Queued" prop="emailQueued" [width]="110">
				<ng-template let-row="row" ngx-datatable-cell-template>
					<span *ngIf="row.isProcessed" [ngClass]="row.emailQueued ? 'text-success' : 'text-muted'">{{ row.emailQueued ? 'Yes' : 'No' }}</span>
				</ng-template>
			</ngx-datatable-column>
			<ngx-datatable-column name="Charge ID" prop="chargeId" [width]="100"></ngx-datatable-column>
			<ngx-datatable-column name="Journal Entries" prop="journalEntryCount" [width]="130" [sortable]="false">
				<ng-template let-row="row" ngx-datatable-cell-template>
					<button
						*ngIf="row.journalEntryCount > 0"
						type="button"
						class="btn btn-link btn-sm p-0"
						(click)="showJournalEntries(row)"
						[attr.data-testid]="'eom-processing-journal-entries-' + row.id">
						<i class="fa fa-book mr-1" aria-hidden="true"></i>View ({{ row.journalEntryCount }})
					</button>
					<span *ngIf="!row.journalEntryCount" class="text-muted">None</span>
				</ng-template>
			</ngx-datatable-column>
		</ngx-datatable>
	</div>
</div>

<!-- Journal entries of one charge -->
<ng-template #journalEntriesModal let-dismiss="dismiss">
	<div class="modal-content" data-testid="eom-processing-journal-entries-modal">
		<div class="modal-header">
			<h4 class="modal-title" *ngIf="journalEntriesCharge">
				Journal Entries for Charge {{ journalEntriesCharge.chargeId }}
			</h4>
			<button
				type="button"
				class="close"
				aria-label="Close"
				(click)="dismiss()"
				data-testid="eom-processing-journal-entries-dismiss">
				<span aria-hidden="true">&times;</span>
			</button>
		</div>
		<div class="modal-body">
			<div class="row mb-3" *ngIf="journalEntriesCharge">
				<div class="col-md-3 col-6">
					<div class="text-muted">Company ID</div>
					<div class="font-weight-bold">{{ journalEntriesCharge.companyId }}</div>
				</div>
				<div class="col-md-3 col-6">
					<div class="text-muted">Invoice</div>
					<div class="font-weight-bold">{{ journalEntriesCharge.invoiceNumber }}</div>
				</div>
				<div class="col-md-3 col-6">
					<div class="text-muted">Category</div>
					<div class="font-weight-bold">{{ journalEntriesCharge.category }}</div>
				</div>
				<div class="col-md-3 col-6">
					<div class="text-muted">Charge Amount</div>
					<div class="font-weight-bold">{{ journalEntriesCharge.chargeAmount | currency : 'USD' : 'symbol' : '1.2-2' }}</div>
				</div>
				<div class="col-12 mt-2">
					<div class="text-muted">Note</div>
					<div>{{ journalEntriesCharge.note }}</div>
				</div>
			</div>

			<div *ngIf="loadingJournalEntries" class="text-center my-3">
				<i class="fa fa-spinner fa-spin mr-2" aria-hidden="true"></i>Loading journal entries...
			</div>
			<div class="alert alert-danger" *ngIf="journalEntriesError">{{ journalEntriesError }}</div>

			<ng-container *ngIf="!loadingJournalEntries && !journalEntriesError">
				<div class="text-muted" *ngIf="journalEntries.length === 0">No journal entries for this charge.</div>
				<table class="table table-sm table-striped mb-0" *ngIf="journalEntries.length > 0">
					<thead>
						<tr>
							<th>ID</th>
							<th>Txn Date</th>
							<th>Type</th>
							<th>Account</th>
							<th>Description</th>
							<th class="text-right">Debit</th>
							<th class="text-right">Credit</th>
						</tr>
					</thead>
					<tbody>
						<tr *ngFor="let je of journalEntries">
							<td>{{ je.id }}</td>
							<td class="text-nowrap">{{ je.txnDate | date : 'MM/dd/yyyy' }}</td>
							<td>{{ je.journalEntryTypeName || je.journalEntryTypeId }}</td>
							<td [title]="je.chartOfAccountDescription || ''">
								<span *ngIf="je.chartOfAccountId">{{ je.chartOfAccountName || je.chartOfAccountId }}</span>
								<span *ngIf="!je.chartOfAccountId" class="text-danger">Missing</span>
							</td>
							<td>{{ je.description }}</td>
							<td class="text-right">{{ je.debitAmount ? (je.debitAmount | currency : 'USD' : 'symbol' : '1.2-4') : '' }}</td>
							<td class="text-right">{{ je.creditAmount ? (je.creditAmount | currency : 'USD' : 'symbol' : '1.2-4') : '' }}</td>
						</tr>
					</tbody>
					<tfoot>
						<tr class="font-weight-bold">
							<td colspan="5">
								Total
								<span class="ml-2" [ngClass]="journalEntriesBalanced ? 'text-success' : 'text-danger'">
									{{ journalEntriesBalanced ? 'Balanced' : 'Credits and debits do not balance' }}
								</span>
							</td>
							<td class="text-right">{{ journalEntriesDebits | currency : 'USD' : 'symbol' : '1.2-4' }}</td>
							<td class="text-right">{{ journalEntriesCredits | currency : 'USD' : 'symbol' : '1.2-4' }}</td>
						</tr>
					</tfoot>
				</table>
			</ng-container>
		</div>
		<div class="modal-footer">
			<button
				type="button"
				class="btn btn-secondary"
				(click)="dismiss()"
				data-testid="eom-processing-journal-entries-close">
				Close
			</button>
		</div>
	</div>
</ng-template>



// WEBPACK FOOTER //
// ./src/app/developer/components/developer-tools/eom-processing/eom-processing.component.html